import { DataAccessUtils } from '@assets/model/DataAccessUtils';
import scoreRulesRaw from './ScoreDefintion.json';
import { z } from 'zod';
import { useStore } from './Store';
import _ from 'lodash';
import { observable } from 'mobx';

const RuleSchema = z
    .object({
        field: z.string(),
        check: z.enum(['greater_than', 'range', 'boolean', 'none']),
        range: z.tuple([z.number(), z.number()]).optional(),
        points: z.number().default(1),
        dataPick: z.enum(['biggest_key_with_value']).optional(),
        threshold: z.number().optional(),
        threshold_display: z.string().optional(),
        inverse_score: z.boolean().default(false),
        sectionId: z.string().optional(),
        disabled: z.boolean().default(false),
        debugger: z.boolean().default(false)
    })
    .refine(data => {
        if (data.check === 'range' && !data.range) {
            return false;
        }

        if (
            data.check === 'greater_than' &&
            (data.threshold === undefined || data.range === undefined)
        ) {
            return false;
        }

        if (data.check !== 'range' && data.inverse_score) {
            return false;
        }

        return true;
    });

const SectionSchema = z.object({
    sectionName: z.string(),
    section_worth: z.number(),
    rules: z.array(RuleSchema)
});

export class ScoreFieldsGroup {
    constructor(sectionId: SectionNameTypes) {
        const scoreRules = useScoreStore().rules;

        this.sectionId = sectionId;
        this.section_worth = scoreRules[sectionId].section_worth;
        this.name = scoreRules[sectionId].sectionName;
        this.fields = ScoreUtils.get_section_fields(sectionId);
    }

    readonly name: string;
    readonly sectionId: SectionNameTypes;
    readonly fields: string[];
    readonly section_worth: number;
}

//type SectionTypes = "revenue_performance" | "ebitda_performance";
const SectionTypeEnum = z.enum([
    'overview',
    'revenue_performance',
    'ebitda_performance',
    'market_size_and_performance',
    'fcf_profile',
    'concentration',
    'other'
]);
export type SectionNameTypes = z.infer<typeof SectionTypeEnum>;

export type SectionType = z.infer<typeof SectionSchema>;
export type RuleType = z.infer<typeof RuleSchema>;

const SectionsSchema = z.record(SectionSchema).refine(data => {
    return Object.keys(data).every(key =>
        SectionTypeEnum.options.includes(key as any)
    );
});

export type SectionsType = z.infer<typeof SectionsSchema>;

const scoreRulesCopy = SectionsSchema.parse(scoreRulesRaw);

export class ScoreUtils {
    constructor(
        private dataAccess: DataAccessUtils,
        private doLog: boolean = false
    ) {}

    get_rule_weight(section: SectionNameTypes, rule: RuleType) {
        const scoreRules = getRules();
        const rulesUsed = scoreRules[section].rules.filter(rule =>
            this.is_rule_used_for_scoring(rule)
        ).length;
        return rule.points / rulesUsed;
    }

    get_datapick_key_for_rule(rule: RuleType) {
        if (rule.dataPick === 'biggest_key_with_value') {
            const fieldValue = this.dataAccess.get_extended_value_float(
                rule.field
            );
            var keys = Object.keys(fieldValue).sort().reverse();
            if (keys.length == 0) {
                return null;
            }

            for (var i = 0; i < keys.length; i++) {
                var keyIndex = keys[i];
                var value = fieldValue[keyIndex];
                if (value != 'N/A' && value != null) {
                    return keyIndex;
                }
            }
        } else {
            return null;
        }
    }
    get_extended_float_for_scoring(score: RuleType): number | string {
        var fieldValue = this.dataAccess.get_extended_value_float(score.field);
        if (score.dataPick) {
            const keyIndex = this.get_datapick_key_for_rule(score);
            if (keyIndex == null) {
                return 'N/A';
            }
            return fieldValue[keyIndex];
        } else {
            return fieldValue;
        }
    }

    do_rule_score(score: RuleType, clamp_to_01: boolean = true) {
        if (score.debugger) {
            debugger;
        }

        const p = score.points;
        var fieldValue = this.get_extended_float_for_scoring(score);
        var ret = 0;
        switch (score.check) {
            case 'greater_than':
                ret = (fieldValue as number) > score.threshold ? p : 0;
                this.log(
                    `Scoring -> (${score.field}) greater_than (${score.threshold}): ${fieldValue}: ${ret}/${p}`
                );
                break;
            case 'range':
                const [low, high] = score.range;
                var norm = ((fieldValue as number) - low) / (high - low);
                if (clamp_to_01) {
                    norm = Math.min(1, Math.max(0, norm));
                }
                if (score.inverse_score === true) {
                    this.log(
                        `*** Inverse Scoring (${score.field}): ${norm} to: ${1 - norm} ***`
                    );
                    norm = 1 - norm;
                }

                ret = p * norm;
                this.log(
                    `Scoring -> (${score.field}) range[${low}, ${high}] (Value: ${(fieldValue as number).toFixed(2)}, Norm: ${norm.toFixed(2)}): ${ret}/${p}`
                );
                break;
            case 'boolean':
                ret = fieldValue ? p : 0;
                this.log(
                    `Scoring -> (${score.field}) boolean: ${fieldValue}: ${ret}/${p}`
                );
                break;
            default:
                this.log('Scoring-> Unknown check type: ' + score.check);
                ret = 0;
                break;
        }

        if (isNaN(ret)) {
            this.log('Scoring-> Error calculating score for ' + score.field);
            ret = 0;
        }

        return ret;
    }

    log(msg: string) {
        if (!this.doLog) {
            return;
        }

        console.log(msg);
    }

    static get_section_name(section: SectionNameTypes) {
        const scoreRules = getRules();
        return scoreRules[section].sectionName;
    }

    static get_section_fields(section: SectionNameTypes) {
        const scoreRules = getRules();
        return scoreRules[section].rules.map(rule => rule.field);
    }

    is_rule_excluded_for_client(rule: RuleType) {
        if (rule.disabled) {
            return true;
        }

        return false;
    }

    is_rule_used_for_scoring(scoreRule: RuleType) {
        if (scoreRule.check === 'none' || scoreRule.disabled) {
            return false;
        }

        if (this.is_rule_excluded_for_client(scoreRule)) {
            this.log(
                'Scoring-> Skipping: ' +
                    scoreRule.field +
                    ' due to client exclusion'
            );
            return false;
        }

        var value = this.get_extended_float_for_scoring(scoreRule);
        if (value == 'N/A' || isNaN(value as number)) {
            this.log(
                'Scoring-> Skipping: ' + scoreRule.field + ' due to N/A or NaN'
            );
            return false;
        }

        return true;
    }

    static get_score_rule_by_fieldname(
        section: SectionNameTypes,
        fieldName: string
    ): RuleType {
        const scoreRules = getRules();
        const sectionData = scoreRules[section];
        return sectionData.rules.find(rule => rule.field === fieldName);
    }

    is_section_used_for_scoring_by_id(section: SectionNameTypes) {
        const scoreRules = getRules();
        return this.is_section_used_for_scoring(scoreRules[section]);
    }

    is_section_used_for_scoring(section: SectionType) {
        var isUsed = false;
        for (const rule of section.rules) {
            if (this.is_rule_used_for_scoring(rule)) {
                isUsed = true;
                break;
            }
        }
        return isUsed;
    }

    calcSectionPercentileById(sectionId: SectionNameTypes) {
        const scoreRules = getRules();
        const section = scoreRules[sectionId];
        return this.calcSectionPercentile(section);
    }

    calcSectionPercentile(section: SectionType) {
        var maxPointsAvailable = 0;
        var totalPoints = 0;
        section.rules.forEach(rule => {
            if (this.is_rule_used_for_scoring(rule)) {
                maxPointsAvailable += rule.points;
                totalPoints += this.do_rule_score(rule);
            }
        });

        this.log(
            `Scoring-> ${section.sectionName} Section: ${totalPoints.toFixed(2)}/${maxPointsAvailable.toFixed(2)}`
        );
        const percentile = totalPoints / maxPointsAvailable;
        this.log(
            `Scoring-> ${section.sectionName} Section Percentile: ${percentile.toFixed(2)}`
        );
        return percentile;
    }

    get_max_points_based_on_sections_used() {
        var maxPoints = 0;
        const scoreRules = getRules();
        for (var id in scoreRules) {
            if (
                this.is_section_used_for_scoring_by_id(id as SectionNameTypes)
            ) {
                maxPoints += scoreRules[id].section_worth;
            }
        }
        return maxPoints;
    }
    calcScorePercentile() {
        const scoreRules = getRules();
        var maxAvailableSectionPoints = 0;
        var totalPointsScored = 0;

        this.log('START SCORE CALCULATION');
        for (var id in scoreRules) {
            const section = scoreRules[id];
            if (this.is_section_used_for_scoring(section)) {
                maxAvailableSectionPoints += section.section_worth;

                const percentileScoreSection =
                    this.calcSectionPercentile(section);
                this.log(
                    `Section ${section.sectionName} scored ${percentileScoreSection}`
                );
                totalPointsScored +=
                    percentileScoreSection * section.section_worth;
            }
        }
        this.log('END SCORE CALCULATION');

        if (maxAvailableSectionPoints == 0) {
            this.log('Scoring-> No sections to score');
            return null;
        }

        var finalScore = totalPointsScored / maxAvailableSectionPoints;
        return finalScore;
    }
}

class ScoreRulesStore {
    rules: SectionsType = null;
    constructor(private outputLog: boolean = false) {}

    init() {
        this.rules = _.cloneDeep(scoreRulesCopy);

        for (var sectionId in this.rules) {
            const section = scoreRulesCopy[sectionId];
            section.rules.forEach(rule => {
                rule.sectionId = sectionId;
            });
        }

        this.setupGroups();
    }

    setupGroups() {
        this.overviewGroup = new ScoreFieldsGroup('overview');
        this.revenueGroup = new ScoreFieldsGroup('revenue_performance');
        this.ebitdaGroup = new ScoreFieldsGroup('ebitda_performance');
        this.marketGroup = new ScoreFieldsGroup('market_size_and_performance');
        this.fcfGroup = new ScoreFieldsGroup('fcf_profile');
        this.customerConcentration = new ScoreFieldsGroup('concentration');
        this.other = new ScoreFieldsGroup('other');

        this.SCORE_FIELDS_GROUPS = [
            this.revenueGroup,
            this.ebitdaGroup,
            this.marketGroup,
            this.fcfGroup
            //this.customerConcentration,
            //this.other
        ];
        this.COMPARE_FIELDS_GROUPS = [
            this.overviewGroup,
            this.revenueGroup,
            this.ebitdaGroup,
            this.marketGroup,
            this.fcfGroup
            //this.customerConcentration,
            //this.other
        ];
    }

    get_local_rule(sectonId: SectionNameTypes, fieldName: string): RuleType {
        return this.rules[sectonId].rules.find(
            rule => rule.field === fieldName
        );
    }

    update_rules(data: SectionsType) {
        this.rules = _.cloneDeep(scoreRulesCopy);

        for (var sectionId in data) {
            const section = data[sectionId];
            const pick = _.pick(section, ['sectionName', 'section_worth']);
            Object.assign(this.rules[sectionId], pick);

            if (section.rules) {
                for (var ruleUpdates of section.rules) {
                    var localRule = this.get_local_rule(
                        sectionId as SectionNameTypes,
                        ruleUpdates.field
                    );
                    if (localRule) {
                        Object.assign(localRule, ruleUpdates);
                    }
                }
            }
        }

        this.setupGroups();
    }

    private overviewGroup: ScoreFieldsGroup;
    private revenueGroup: ScoreFieldsGroup;
    private ebitdaGroup: ScoreFieldsGroup;
    private marketGroup: ScoreFieldsGroup;
    private fcfGroup: ScoreFieldsGroup;
    private customerConcentration: ScoreFieldsGroup;
    private other: ScoreFieldsGroup;

    @observable accessor SCORE_FIELDS_GROUPS: ScoreFieldsGroup[] = [];
    @observable accessor COMPARE_FIELDS_GROUPS: ScoreFieldsGroup[] = [];
}

const scoringDataStore = new ScoreRulesStore();
export const useScoreStore = () => {
    return scoringDataStore;
};

export const getRules = () => {
    return scoringDataStore.rules;
};

scoringDataStore.init();
